// LAB-ONLY candidate alternates for the Animality ("Beast!") finisher —
// previewed on /vfx-lab for approval, NOT wired into the game. Same uniform
// contract as animality.frag.ts (uTime / uDuration / uAspect / uCenter).

// Claw! — three diagonal beast slashes tear across the screen in sequence,
// each sweeping top-to-bottom with a hot white core, blue rim glow, a gentle
// arc, and an entry spark where the tear begins.
export const ANIMALITY_CLAW_FRAG = /* glsl */ `
  precision mediump float;
  varying vec2 vUv;
  uniform float uTime;
  uniform float uDuration;
  uniform vec2 uAspect;
  uniform vec2 uCenter;

  void main() {
    vec2 uv = ((vUv - uCenter) * 2.0) * uAspect;
    float t = clamp(uTime / uDuration, 0.0, 1.0);

    // Rotate into slash space (~ -34deg) so the tears read as one paw swipe.
    float cA = 0.828;
    float sA = -0.56;
    vec2 p = mat2(cA, sA, -sA, cA) * uv;

    float slashes = 0.0;
    float sparks = 0.0;
    for (int i = 0; i < 3; i++) {
      float fi = float(i);
      float off = (fi - 1.0) * 0.3;
      float si = 0.1 + 0.14 * fi;
      float prog = smoothstep(si, si + 0.09, t);
      if (prog > 0.0) {
        float tip = mix(0.8, -0.8, prog);
        float xo = off + 0.18 * p.y * p.y; // gentle arc
        float lenMask = 1.0 - smoothstep(0.72, 0.82, abs(p.y));
        float drawn = smoothstep(tip - 0.03, tip + 0.05, p.y) * lenMask;
        float core = exp(-pow((p.x - xo) * 30.0, 2.0));
        float glowX = exp(-pow((p.x - xo) * 8.0, 2.0)) * 0.45;
        float decay = exp(-2.2 * max(t - (si + 0.12), 0.0));
        slashes += (core + glowX) * drawn * decay;
        vec2 ep = p - vec2(off + 0.115, 0.8);
        sparks += exp(-dot(ep, ep) * 60.0) * exp(-16.0 * max(t - si, 0.0));
      }
    }

    float fade = 1.0 - smoothstep(0.82, 1.0, t);
    vec3 white = vec3(1.0);
    vec3 blue = vec3(0.25, 0.65, 1.0);
    vec3 col = (mix(white, blue, 0.35) * slashes + white * sparks) * fade;
    float alpha = clamp(slashes + sparks, 0.0, 1.0) * fade;
    gl_FragColor = vec4(col, alpha);
  }
`

// Howl! — a sonic roar: wavy concentric rings emitted continuously from a
// pulsing mint core, over fur-like radial streaks. Ring waviness and streaks
// use sin(a*K) with integer K, so the pattern is seamless across the -x axis.
export const ANIMALITY_HOWL_FRAG = /* glsl */ `
  precision mediump float;
  varying vec2 vUv;
  uniform float uTime;
  uniform float uDuration;
  uniform vec2 uAspect;
  uniform vec2 uCenter;

  void main() {
    vec2 uv = ((vUv - uCenter) * 2.0) * uAspect;
    float r = length(uv);
    float a = atan(uv.y, uv.x);
    float t = clamp(uTime / uDuration, 0.0, 1.0);
    float tt = uTime;

    // Wavy radius so the rings ripple like a roar, not clean circles.
    float rw = r + 0.035 * sin(a * 9.0 - tt * 3.0) + 0.02 * sin(a * 5.0 + tt * 2.2);

    // 4 rings continuously re-emitted from the core, fading as they travel.
    float rings = 0.0;
    for (int k = 0; k < 4; k++) {
      float ph = fract(tt * 0.45 - float(k) * 0.25);
      rings += exp(-pow((rw - ph * 1.5) * 10.0, 2.0)) * (1.0 - ph);
    }

    float core = exp(-r * 4.5) * (0.55 + 0.45 * sin(tt * 8.0));
    float streaks = pow(0.5 + 0.5 * sin(a * 24.0 + sin(r * 6.0 + tt * 1.5)), 3.0)
      * exp(-2.6 * r) * 0.5;

    float env = smoothstep(0.0, 0.2, t) * (1.0 - smoothstep(0.82, 1.0, t));

    vec3 mint = vec3(0.063, 0.957, 0.545);
    vec3 blue = vec3(0.25, 0.65, 1.0);
    vec3 col = (mix(mint, blue, clamp(r, 0.0, 1.0)) * rings * 1.3
      + mint * core
      + blue * streaks) * env;
    float alpha = clamp(rings + core * 0.8 + streaks, 0.0, 1.0) * env;
    gl_FragColor = vec4(col, alpha);
  }
`

// Stampede! — three giant paw prints stamp across the screen in sequence,
// each punching in with a scale overshoot, a white impact ring, and faint
// rushing speed-lines behind. Paw = union of 5 soft ellipses (pad + 4 toes).
export const ANIMALITY_STAMPEDE_FRAG = /* glsl */ `
  precision mediump float;
  varying vec2 vUv;
  uniform float uTime;
  uniform float uDuration;
  uniform vec2 uAspect;
  uniform vec2 uCenter;

  float ellip(vec2 p, vec2 c, vec2 s) {
    vec2 q = (p - c) / s;
    return dot(q, q);
  }

  float paw(vec2 p) {
    float d = ellip(p, vec2(0.0, -0.07), vec2(0.17, 0.14));
    d = min(d, ellip(p, vec2(-0.135, 0.1), vec2(0.055, 0.075)));
    d = min(d, ellip(p, vec2(-0.048, 0.16), vec2(0.055, 0.08)));
    d = min(d, ellip(p, vec2(0.048, 0.16), vec2(0.055, 0.08)));
    d = min(d, ellip(p, vec2(0.135, 0.1), vec2(0.055, 0.075)));
    return 1.0 - smoothstep(0.72, 1.0, d);
  }

  // Returns vec2(pawMask, impactRing) for one stamp.
  vec2 stamp(vec2 uv, vec2 pos, float an, float s0, float t) {
    float ap = smoothstep(s0, s0 + 0.05, t);
    if (ap <= 0.0) return vec2(0.0);
    float sc = mix(1.7, 1.0, smoothstep(s0, s0 + 0.09, t)); // punch-in
    float c = cos(an);
    float s = sin(an);
    vec2 p = (mat2(c, s, -s, c) * (uv - pos)) / sc;
    float dt = max(t - s0, 0.0);
    float ring = (1.0 - smoothstep(0.0, 0.05, abs(length(uv - pos) - dt * 2.2)))
      * exp(-7.0 * dt);
    return vec2(paw(p) * ap, ring);
  }

  void main() {
    vec2 uv = ((vUv - uCenter) * 2.0) * uAspect;
    float t = clamp(uTime / uDuration, 0.0, 1.0);
    float tt = uTime;

    vec2 s1 = stamp(uv, vec2(-0.52, -0.14), 0.28, 0.1, t);
    vec2 s2 = stamp(uv, vec2(0.02, 0.12), -0.18, 0.32, t);
    vec2 s3 = stamp(uv, vec2(0.54, -0.06), 0.22, 0.54, t);
    float paws = s1.x + s2.x + s3.x;
    float rings = s1.y + s2.y + s3.y;

    // Faint horizontal speed-lines rushing left-to-right behind the stamps.
    float lines = pow(0.5 + 0.5 * sin(uv.y * 30.0 + 1.3), 6.0)
      * (0.5 + 0.5 * sin(uv.x * 3.0 - tt * 9.0))
      * (1.0 - smoothstep(0.6, 0.9, abs(uv.y))) * 0.3;

    float fade = 1.0 - smoothstep(0.85, 1.0, t);
    float breath = 0.85 + 0.15 * sin(tt * 7.0);

    vec3 white = vec3(1.0);
    vec3 mint = vec3(0.063, 0.957, 0.545);
    vec3 blue = vec3(0.25, 0.65, 1.0);
    vec3 col = (mix(blue, mint, 0.4) * paws * breath
      + white * rings * 0.9
      + blue * lines) * fade;
    float alpha = clamp(paws * 0.95 + rings + lines, 0.0, 1.0) * fade;
    gl_FragColor = vec4(col, alpha);
  }
`
